import React, { useState, useEffect, useRef } from "react";
import { Home, Building2, Plus, Calculator, MoreHorizontal, X, Mic, Camera, FileText, DollarSign, Sparkles } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { triggerHaptic } from "@/hooks/useHaptic";
import { MoreMenu } from "@/components/mobile/MoreMenu";
import { DiarioObrasModal } from "@/components/DiarioObrasModal";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

interface NavItem {
  icon?: React.ElementType;
  label?: string;
  href?: string;
  type?: 'fab' | 'more';
}

const navItems: NavItem[] = [
  { icon: Home, label: "Painel", href: "/dashboard" },
  { icon: Building2, label: "Projetos", href: "/projects" },
  { type: "fab" }, // Central FAB placeholder
  { icon: Calculator, label: "Financeiro", href: "/financeiro" },
  { icon: MoreHorizontal, label: "Mais", type: "more" },
];

interface MobileNavigationProps {
  onFABClick?: () => void;
  onOpenAIChat?: () => void;
}

export function MobileNavigation({ onFABClick, onOpenAIChat }: MobileNavigationProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [fabExpanded, setFabExpanded] = useState(false);

  // State for Quick Actions
  const [showDiarioModal, setShowDiarioModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Project Selection Logic
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [userProjects, setUserProjects] = useState<any[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<'camera' | 'diary' | null>(null);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Extract Project ID from URL if available
  const getProjectIdFromUrl = () => {
    const match = location.pathname.match(/\/projetos\/([^\/]+)/) || location.pathname.match(/\/projeto\/([^\/]+)/);
    return match ? match[1] : null;
  };

  const urlProjectId = getProjectIdFromUrl();
  const activeProjectId = urlProjectId || selectedProjectId;

  // Fetch projects for selector
  useEffect(() => {
    const fetchProjects = async () => {
      setLoadingProjects(true);
      try {
        const { data, error } = await supabase
          .from('projects')
          .select('id, name, client')
          .eq('status', 'em_andamento')
          .order('updated_at', { ascending: false });

        if (!error && data) {
          setUserProjects(data);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoadingProjects(false);
      }
    };

    if (showProjectSelector) {
      fetchProjects();
    }
  }, [showProjectSelector]);

  const handleProjectSelect = (projectId: string) => {
    setSelectedProjectId(projectId);
    setShowProjectSelector(false);

    // Resume pending action
    if (pendingAction === 'diary') {
      setShowDiarioModal(true);
    } else if (pendingAction === 'camera') {
      setShowDiarioModal(true);
    }
    setPendingAction(null);
  };

  const handleCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setSelectedImages(files);

      if (urlProjectId) {
        setShowDiarioModal(true);
      } else {
        setPendingAction('camera');
        setShowProjectSelector(true);
      }
    }
  };

  const handleOpenDiario = () => {
    if (urlProjectId) {
      setShowDiarioModal(true);
    } else {
      setPendingAction('diary');
      setShowProjectSelector(true);
    }
  };

  const handleMoreClick = () => {
    triggerHaptic('light');
    setMoreMenuOpen(true);
  };

  const handleFABClick = () => {
    triggerHaptic('medium');
    setFabExpanded(!fabExpanded);
  };

  const quickActions = [
    {
      icon: DollarSign,
      label: 'Despesa',
      color: 'bg-emerald-500',
      action: () => {
        setFabExpanded(false);
        navigate('/financeiro');
      },
    },
    {
      icon: Mic,
      label: 'Diário',
      color: 'bg-violet-500',
      action: () => {
        setFabExpanded(false);
        handleOpenDiario();
      },
    },
    {
      icon: Sparkles,
      label: 'IA',
      color: 'bg-primary',
      action: () => {
        setFabExpanded(false);
        onOpenAIChat?.();
      },
    },
    {
      icon: FileText,
      label: 'Projeto',
      color: 'bg-blue-500',
      action: () => {
        setFabExpanded(false);
        navigate('/projetos/novo');
      },
    },
  ];

  return (
    <>
      {/* Backdrop when FAB expanded */}
      <AnimatePresence>
        {fabExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/60 backdrop-blur-sm z-40 md:hidden"
            onClick={() => setFabExpanded(false)}
          />
        )}
      </AnimatePresence>

      {/* Quick Actions Menu */}
      <AnimatePresence>
        {fabExpanded && (
          <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 md:hidden flex gap-4">
            {quickActions.map((action, index) => (
              <motion.button
                key={action.label}
                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.8 }}
                transition={{ delay: index * 0.05, type: "spring", stiffness: 400 }}
                onClick={() => {
                  triggerHaptic('light');
                  action.action();
                }}
                className={cn(
                  "flex flex-col items-center gap-1.5 p-3 rounded-2xl",
                  "bg-background/90 backdrop-blur-md",
                  "border border-border/50 shadow-lg",
                  "active:scale-95 transition-transform"
                )}
              >
                <div className={cn(
                  "w-12 h-12 rounded-xl flex items-center justify-center",
                  action.color,
                  "text-white"
                )}>
                  <action.icon className="h-5 w-5" />
                </div>
                <span className="text-[10px] font-medium text-muted-foreground">{action.label}</span>
              </motion.button>
            ))}
          </div>
        )}
      </AnimatePresence>

      <nav 
        className={cn(
          "md:hidden fixed bottom-0 left-0 right-0 z-50",
          "bg-background/95 backdrop-blur-xl",
          "border-t border-border/30"
        )}
        style={{
          paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)',
        }}
      >
        {/* Glassmorphism gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/70 to-transparent pointer-events-none" />

        <div className="relative flex items-center justify-around h-[64px] px-2">
          {navItems.map((item, index) => {
            // Central FAB
            if (item.type === 'fab') {
              return (
                <div key="fab" className="relative -mt-5" data-tour="fab-button">
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={handleFABClick}
                    className={cn(
                      "w-14 h-14 rounded-2xl",
                      "bg-gradient-to-br from-primary to-primary/80",
                      "flex items-center justify-center",
                      "shadow-lg shadow-primary/30",
                      "border-4 border-background"
                    )}
                  >
                    <motion.div
                      animate={{ rotate: fabExpanded ? 45 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {fabExpanded ? (
                        <X className="h-6 w-6 text-primary-foreground" />
                      ) : (
                        <Plus className="h-6 w-6 text-primary-foreground" />
                      )}
                    </motion.div>
                  </motion.button>
                </div>
              );
            }

            // "More" button
            if (item.type === 'more') {
              return (
                <button
                  key="more"
                  onClick={handleMoreClick}
                  className={cn(
                    "relative flex flex-col items-center justify-center gap-1",
                    "py-2 px-4 rounded-2xl transition-all duration-300",
                    "min-w-[60px] min-h-[56px]",
                    "active:scale-90"
                  )}
                >
                  <motion.div className="relative">
                    <MoreHorizontal className="h-5 w-5 text-muted-foreground" />
                  </motion.div>
                  <span className="text-[10px] font-medium text-muted-foreground opacity-70">
                    {item.label}
                  </span>
                </button>
              );
            }

            // Regular nav items
            if (!item.href || !item.icon) return null;

            const isActive = location.pathname === item.href ||
              (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
            const Icon = item.icon;

            return (
              <NavLink
                key={item.href}
                to={item.href}
                className={cn(
                  "relative flex flex-col items-center justify-center gap-1",
                  "py-2 px-4 rounded-2xl transition-all duration-300",
                  "min-w-[60px] min-h-[56px]",
                  "active:scale-90"
                )}
              >
                {/* Active background with animation */}
                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      layoutId="activeNavTab"
                      className="absolute inset-0 bg-primary/15 rounded-2xl"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}
                </AnimatePresence>

                {/* Icon with bounce animation */}
                <motion.div
                  className="relative"
                  animate={{
                    y: isActive ? -2 : 0,
                    scale: isActive ? 1.1 : 1
                  }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                >
                  <Icon className={cn(
                    "h-5 w-5 transition-colors duration-200",
                    isActive ? "text-primary" : "text-muted-foreground"
                  )} />
                </motion.div>

                {/* Label */}
                <motion.span
                  className={cn(
                    "text-[10px] font-medium transition-colors duration-200",
                    isActive ? "text-primary font-semibold" : "text-muted-foreground"
                  )}
                  animate={{
                    opacity: isActive ? 1 : 0.7
                  }}
                >
                  {item.label}
                </motion.span>

                {/* Active indicator dot */}
                <AnimatePresence>
                  {isActive && (
                    <motion.span
                      className="absolute -bottom-0.5 w-1 h-1 bg-primary rounded-full"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                </AnimatePresence>
              </NavLink>
            );
          })}
        </div>
      </nav>

      <MoreMenu open={moreMenuOpen} onOpenChange={setMoreMenuOpen} />

      {/* Hidden Camera Input */}
      <input
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        ref={cameraInputRef}
        onChange={handleCameraCapture}
      />

      {/* Global Diario Modal */}
      {activeProjectId && (
        <DiarioObrasModal
          isOpen={showDiarioModal}
          onClose={() => {
            setShowDiarioModal(false);
            setSelectedImages([]);
            setSelectedProjectId(null); // Reset manually selected project on close
          }}
          projectId={activeProjectId}
          onSuccess={() => {
            // Success logic
          }}
          initialFiles={selectedImages}
        />
      )}

      {/* Project Selector Dialog */}
      <Dialog open={showProjectSelector} onOpenChange={setShowProjectSelector}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Selecione a Obra</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2 max-h-[60vh] overflow-y-auto">
            {loadingProjects ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : userProjects.length > 0 ? (
              userProjects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => handleProjectSelect(project.id)}
                  className="w-full text-left p-4 rounded-xl border bg-card hover:bg-accent/50 transition-colors flex items-center justify-between group"
                >
                  <div>
                    <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors">{project.name}</h4>
                    <p className="text-sm text-muted-foreground">{project.client || 'Sem cliente'}</p>
                  </div>
                  <div className="h-2 w-2 rounded-full bg-green-500" />
                </button>
              ))
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <p>Nenhuma obra em andamento encontrada.</p>
                <button
                  onClick={() => {
                    setShowProjectSelector(false);
                    navigate('/projects/novo');
                  }}
                  className="text-primary hover:underline mt-2 text-sm"
                >
                  Criar nova obra
                </button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
